/**
 * Recovers the session from chunk / dynamic-import load failures by reloading
 * the page once. Covers three distinct failure sources:
 *
 *  1. Vite module-preload failures of THIS app's own hashed chunks
 *     (`vite:preloadError`) — typically a client on a stale index.html after a
 *     new deploy.
 *  2. webpack `ChunkLoadError` thrown by third-party bundles that lazy-load
 *     their own chunks at runtime — notably Clerk (`clerk.browser.js` fetching
 *     `framework_clerk.browser_*.js` from the Clerk Frontend API domain). These
 *     surface as window `error` / `unhandledrejection` events, NOT as
 *     `vite:preloadError`.
 *  3. Native dynamic `import()` failures ("Failed to fetch dynamically imported
 *     module"; Safari's "Importing a module script failed").
 *
 * A single guarded reload pulls a fresh entry + chunk map, recovering
 * stale-deploy and transient-network cases. A timestamp cooldown prevents
 * reload loops if the chunk is permanently unavailable.
 */

const RELOAD_TIMESTAMP_KEY = 'chunk-error-reloaded-at';
const RELOAD_COOLDOWN_MS = 10_000;

let installed = false;

/** True if `error` looks like a chunk / dynamic-import load failure. */
export function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false;

  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String((error as { name?: unknown }).name ?? '')
      : '';
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : String(error);

  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [^\s]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/** Reload at most once per cooldown window, so recovery can't become a loop. */
function reloadOnceWithinCooldown(): void {
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
  } catch {
    // sessionStorage read failed (private mode / sandboxed webview). Without a
    // readable guard we can't tell whether we just reloaded, so we must not
    // reload again — see the persistence check below.
    return;
  }

  const now = Date.now();
  if (last && now - last < RELOAD_COOLDOWN_MS) {
    // Already reloaded very recently and the error recurred — the reload did
    // not fix it; stop here to avoid an infinite reload loop.
    return;
  }

  let persisted = false;
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
    persisted = true;
  } catch {
    /* persistence failed — leave persisted false (see below) */
  }

  // Only reload if the cooldown marker was actually stored. If we can't persist
  // it, the next load would read 0 again and reload unconditionally → endless
  // reload loop. Without a working guard, skip the reload entirely.
  if (persisted) {
    window.location.reload();
  }
}

/**
 * Install global listeners that auto-recover from chunk / dynamic-import load
 * failures. Idempotent and browser-only. Call once, as early as possible, from
 * each app entry (`main.tsx`), before rendering.
 */
export function installChunkErrorRecovery(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1. Vite's own module-preload failures (this app's chunks). Vite recommends
  //    calling preventDefault() to stop the unhandled throw, then recovering.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnceWithinCooldown();
  });

  // 2. webpack ChunkLoadError (e.g. Clerk) and synchronous import failures.
  //    Do NOT preventDefault here — let PostHog still capture the exception so
  //    we keep measuring whether this fix reduces occurrences.
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      reloadOnceWithinCooldown();
    }
  });

  // 3. Rejected dynamic import() / async chunk-load promises.
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceWithinCooldown();
    }
  });
}
